import { ViewTypes } from '~/constants/ui.constants';
import { getFromState, useVariant } from '~/composables';
import { mapBlog } from '~/units/blogs/store';
import { ComponentMixin } from '~/mixins/component.mixin';
import type { BlogItem, Blogs, BlogsRepo } from '~/units/blogs/types';
import { LoadingStates, RepoStates } from '~/units/core/types';

export const BlogsMixin = defineNuxtComponent({
  mixins: [ComponentMixin],

  data() {
    return {
      ViewTypes,
    };
  },

  computed: {
    notFound() {
      const route = useRoute();
      const keyword = route.query.q;
      return {
        title: keyword ? `No Results for "${keyword}"` : 'No Results',
        description: `Oops! It seems there are no results ${
          keyword ? `for "${keyword}"` : ''
        }. Feel free to try a different keyword or check out our related articles. `,
      };
    },

    isBlogsLoading() {
      return <boolean>getFromState(LoadingStates.IS_BLOGS_LOADING);
    },

    blogsRepoRef(): Blogs | undefined | null {
      const variant = useVariant(this.component);

      const data: BlogsRepo = <BlogsRepo>getFromState(RepoStates.BLOGS_REPO);
      const route = useRoute();
      const keyword = route.query.q;
      const identifier = `${variant}-${route.query.page ? route.query.page : 1}${keyword ? `-${keyword}` : ''}`;

      return data && data.length > 0 ? data.find((item) => item.identifier === identifier) : null;
    },

    activePage() {
      if (this.blogsRepoRef && this.blogsRepoRef.query && this.blogsRepoRef.query.activePage) {
        return +this.blogsRepoRef.query.activePage;
      }
      return 1;
    },

    pageCount() {
      return this.total < this.limit ? 1 : Math.ceil(this.total / this.limit);
    },

    pages(): {
      id: number | '...';
      is_clickable: boolean;
      title: string | number | '...';
    }[] {
      const keepLength = false;
      const limit = this.blogsRepoRef?.query.limit || 3;

      if (limit === -1) {
        return [];
      }

      const current = this.activePage;
      const size = keepLength;
      const last = this.pageCount;
      const delta = 3;
      const left = current - delta;
      const right = current + delta;
      const leftPad = (delta + 2) * 2;
      const rightPad = (delta + 2) * 2 - 1;
      const range = [];
      const pages: {
        id: number | '...';
        title: number | '...';
        is_clickable: boolean;
      }[] = [];
      let l: number;

      for (let i = 1; i <= last; i++) {
        // Item is first or last
        if (i === 1 || i === last) {
          range.push(i);
        }
        // Item is within the delta
        else if (i >= left && i <= right) {
          range.push(i);
        }
        // Item is before max left padding
        else if (size && i < leftPad && current < leftPad - 2) {
          range.push(i);
        }
        // Item is after max right padding
        else if (size && i > last - rightPad && current > last - rightPad + 2) {
          range.push(i);
        }
      }

      range.forEach(function (i) {
        if (l) {
          if (i - l === 2) {
            pages.push({
              id: l + 1,
              title: l + 1,
              is_clickable: true,
            });
          } else if (i - l !== 1) {
            pages.push({
              id: '...',
              title: '...',
              is_clickable: false,
            });
          }
        }
        pages.push({
          id: i,
          title: i,
          is_clickable: true,
        });
        l = i;
      });

      return pages;
    },

    limit(): number {
      if (this.blogsRepoRef && this.blogsRepoRef.query && this.blogsRepoRef.query.limit) {
        return this.blogsRepoRef.query.limit;
      }
      return 0;
    },

    offset(): number {
      if (this.blogsRepoRef && this.blogsRepoRef.query && this.blogsRepoRef.query.offset) {
        return this.blogsRepoRef.query.offset;
      }
      return 0;
    },

    total(): number {
      if (this.blogsRepoRef && this.blogsRepoRef.total) {
        return this.blogsRepoRef.total;
      }
      return 0;
    },

    blogs() {
      const blogs: Blogs = <Blogs>this.blogsRepoRef;

      if (blogs && blogs.data) {
        return blogs.data.map((item: BlogItem) => {
          return mapBlog(item);
        });
      }
      return [];
    },
  },

  methods: {
    onClickedPage(page: { id: number }) {
      const route = useRoute();

      this.redirectTo({
        path: route.path,
        query: {
          ...route.query,
          page: page.id,
        },
      });
    },

    onClickedNavigationArrow(effect: 1 | -1) {
      this.onClickedPage({
        id: this.activePage + effect,
      });
    },

    /*
    onClickedBlog(blog) {
      const { setData } = useDataFromState('blog');
      setData({
        [blog.url]: blog.original,
      });
    },
     */
  },
});
